import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import '../styles/Testimonials.css';

const Testimonials = ({ translations, language }) => {
  const [ref, inView] = useInView({ threshold: 0.3, triggerOnce: true });
  const [currentIndex, setCurrentIndex] = useState(0);

  const testimonialsData = {
    en: {
      title: 'Testimonials',
      subtitle: 'What Clients Say About Zezo',
      testimonials: [
        {
          id: 1,
          name: 'Sarah Johnson',
          position: 'CEO, TechStart Inc.',
          company: 'TechStart Inc.',
          rating: 5,
          text: 'Zezo transformed our digital marketing completely. Our ROI increased by 400% in just 6 months. His futuristic approach and data-driven strategies are unmatched.',
          avatar: 'SJ',
          color: '#8b5cf6'
        },
        {
          id: 2,
          name: 'Ahmed Al-Rashid',
          position: 'Marketing Director, Gulf Enterprises',
          company: 'Gulf Enterprises',
          rating: 5,
          text: 'Working with Zezo was a game-changer. His expertise in both English and Arabic markets helped us expand globally while maintaining local relevance.',
          avatar: 'AR',
          color: '#06b6d4'
        },
        {
          id: 3,
          name: 'Maria Rodriguez',
          position: 'Founder, EcoShop',
          company: 'EcoShop',
          rating: 5,
          text: 'Zezo\'s innovative marketing strategies helped us achieve 300% growth in online sales. His attention to detail and creative solutions are exceptional.',
          avatar: 'MR',
          color: '#10b981'
        },
        {
          id: 4,
          name: 'David Chen',
          position: 'VP Marketing, FinanceFlow',
          company: 'FinanceFlow',
          rating: 5,
          text: 'The results speak for themselves - 250% increase in qualified leads and 60% reduction in acquisition costs. Zezo is a true marketing visionary.',
          avatar: 'DC',
          color: '#f59e0b'
        },
        {
          id: 5,
          name: 'Lisa Thompson',
          position: 'CMO, HealthTech Solutions',
          company: 'HealthTech Solutions',
          rating: 5,
          text: 'Zezo\'s futuristic approach to healthcare marketing helped us reach new audiences and improve patient engagement by 180%. Highly recommended!',
          avatar: 'LT',
          color: '#f5576c'
        }
      ]
    },
    ar: {
      title: 'التوصيات',
      subtitle: 'ماذا يقول العملاء عن زيزو',
      testimonials: [
        {
          id: 1,
          name: 'سارة جونسون',
          position: 'الرئيس التنفيذي، تك ستارت',
          company: 'تك ستارت',
          rating: 5,
          text: 'زيزو غير تسويقنا الرقمي بالكامل. زاد عائد الاستثمار بنسبة 400% في 6 أشهر فقط. نهجه المستقبلي واستراتيجياته المدفوعة بالبيانات لا مثيل لها.',
          avatar: 'SJ',
          color: '#8b5cf6'
        },
        {
          id: 2,
          name: 'أحمد الراشد',
          position: 'مدير التسويق، مؤسسات الخليج',
          company: 'مؤسسات الخليج',
          rating: 5,
          text: 'العمل مع زيزو كان نقطة تحول. خبرته في الأسواق الإنجليزية والعربية ساعدتنا على التوسع عالمياً مع الحفاظ على الصلة المحلية.',
          avatar: 'AR',
          color: '#06b6d4'
        },
        {
          id: 3,
          name: 'ماريا رودريغيز',
          position: 'المؤسس، إيكو شوب',
          company: 'إيكو شوب',
          rating: 5,
          text: 'استراتيجيات زيزو التسويقية المبتكرة ساعدتنا على تحقيق نمو 300% في المبيعات عبر الإنترنت. اهتمامه بالتفاصيل وحلوله الإبداعية استثنائية.',
          avatar: 'MR',
          color: '#10b981'
        },
        {
          id: 4,
          name: 'ديفيد تشين',
          position: 'نائب رئيس التسويق، فاينانس فلو',
          company: 'فاينانس فلو',
          rating: 5,
          text: 'النتائج تتحدث عن نفسها - زيادة 250% في العملاء المحتملين المؤهلين وانخفاض 60% في تكاليف الاكتساب. زيزو رؤية تسويقية حقيقية.',
          avatar: 'DC',
          color: '#f59e0b'
        },
        {
          id: 5,
          name: 'ليزا طومسون',
          position: 'مدير التسويق الأول، حلول الصحة التقنية',
          company: 'حلول الصحة التقنية',
          rating: 5,
          text: 'نهج زيزو المستقبلي لتسويق الرعاية الصحية ساعدنا على الوصول إلى جماهير جديدة وتحسين مشاركة المرضى بنسبة 180%. موصى به بشدة!',
          avatar: 'LT',
          color: '#f5576c'
        }
      ]
    }
  };

  const data = testimonialsData[language];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => 
        prevIndex === data.testimonials.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000);

    return () => clearInterval(interval);
  }, [data.testimonials.length]);

  const nextTestimonial = () => {
    setCurrentIndex(currentIndex === data.testimonials.length - 1 ? 0 : currentIndex + 1);
  };

  const prevTestimonial = () => {
    setCurrentIndex(currentIndex === 0 ? data.testimonials.length - 1 : currentIndex - 1);
  };

  const goToTestimonial = (index) => {
    setCurrentIndex(index);
  };

  return (
    <section id="testimonials" className="testimonials-section section" ref={ref}>
      <div className="container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <h2 className="section-title gradient-text">{data.title}</h2>
          <p className="section-subtitle">{data.subtitle}</p>
        </motion.div>

        <motion.div
          className="testimonials-container"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={inView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <div className="testimonials-carousel">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                className="testimonial-card glass-morphism"
                initial={{ opacity: 0, x: 100, rotateY: 90 }}
                animate={{ opacity: 1, x: 0, rotateY: 0 }}
                exit={{ opacity: 0, x: -100, rotateY: -90 }}
                transition={{ duration: 0.6, ease: "easeInOut" }}
              >
                <div className="testimonial-content">
                  <div className="quote-icon">
                    <i className="fas fa-quote-left"></i>
                  </div>
                  
                  <p className="testimonial-text">
                    {data.testimonials[currentIndex].text}
                  </p>

                  <div className="testimonial-rating">
                    {[...Array(data.testimonials[currentIndex].rating)].map((_, i) => (
                      <motion.i
                        key={i}
                        className="fas fa-star"
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.3, delay: i * 0.1 }}
                      />
                    ))}
                  </div>

                  <div className="testimonial-author">
                    <div 
                      className="author-avatar"
                      style={{ background: data.testimonials[currentIndex].color }}
                    >
                      {data.testimonials[currentIndex].avatar}
                    </div>
                    <div className="author-info">
                      <h4 className="author-name">{data.testimonials[currentIndex].name}</h4>
                      <p className="author-position">{data.testimonials[currentIndex].position}</p>
                      <p className="author-company">{data.testimonials[currentIndex].company}</p>
                    </div>
                  </div>
                </div>

                <div 
                  className="testimonial-glow"
                  style={{ 
                    background: `radial-gradient(circle, ${data.testimonials[currentIndex].color}20 0%, transparent 70%)` 
                  }}
                ></div>
              </motion.div>
            </AnimatePresence>

            <div className="carousel-controls">
              <motion.button
                className="carousel-btn prev"
                onClick={prevTestimonial}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <i className="fas fa-chevron-left"></i>
              </motion.button>

              <motion.button
                className="carousel-btn next"
                onClick={nextTestimonial}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <i className="fas fa-chevron-right"></i>
              </motion.button>
            </div>
          </div>

          <div className="testimonials-dots">
            {data.testimonials.map((_, index) => (
              <motion.button
                key={index}
                className={`dot ${index === currentIndex ? 'active' : ''}`}
                onClick={() => goToTestimonial(index)}
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.8 }}
              />
            ))}
          </div>
        </motion.div>

        <motion.div
          className="testimonials-stats"
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <div className="stat-item">
            <div className="stat-number">98%</div>
            <div className="stat-label">{language === 'en' ? 'Client Satisfaction' : 'رضا العملاء'}</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">5.0</div>
            <div className="stat-label">{language === 'en' ? 'Average Rating' : 'متوسط التقييم'}</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">150+</div>
            <div className="stat-label">{language === 'en' ? 'Happy Clients' : 'عميل سعيد'}</div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Testimonials;